import { useEffect, useState } from "react";
import { ChevronDown, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const pts = (n) => `${n > 0 ? "+" : ""}${n.toLocaleString("de-DE")}`;

function NameList({ reason }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/compensations/list", { params: { reason } }).then(({ data }) => setList(data)).catch((e) => { setList([]); toast.error(formatErr(e)); }); }, [reason]);
  if (!list) return <Loader2 className="w-5 h-5 animate-spin text-rose-400 m-3" />;
  const shown = list.filter((r) => !q || (r.user?.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="mt-3 space-y-2">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name suchen …" className="w-full h-10 rounded-xl border border-pink-200 px-3 text-sm" data-testid="comp-search" />
      <ul className="max-h-80 overflow-y-auto divide-y divide-pink-50 rounded-xl border border-pink-100 bg-white" data-testid="comp-names">
        {shown.map((r, i) => (
          <li key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
            {r.user?.picture ? <img src={r.user.picture} alt="" className="w-7 h-7 rounded-full object-cover" /> : <span className="w-7 h-7 rounded-full bg-pink-100" />}
            <span className="flex-1 min-w-0 truncate font-semibold">{r.user?.name || "Gelöschtes Konto"}</span>
            <span className="text-xs text-slate-400 hidden sm:inline">{fmt(r.at)}</span>
            <span className={`font-extrabold tabular-nums ${r.amount < 0 ? "text-red-600" : "text-emerald-600"}`}>{pts(r.amount)}</span>
          </li>
        ))}
        {!shown.length && <li className="px-3 py-3 text-sm text-slate-400">Keine Treffer</li>}
      </ul>
    </div>
  );
}

function CompRow({ r, i }) {
  const [open, setOpen] = useState(false);
  const minus = r.sum < 0;
  const Icon = minus ? TrendingDown : TrendingUp;
  return (
    <div className="rounded-2xl border border-pink-100 bg-white p-4" data-testid={`comp-row-${i}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-start gap-3 text-left" data-testid={`comp-toggle-${i}`}>
        <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${minus ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}><Icon className="w-5 h-5" /></span>
        <span className="flex-1 min-w-0">
          <span className="block font-bold">{r.reason}</span>
          <span className="block text-xs text-slate-500">{fmt(r.first)}{r.last !== r.first ? ` – ${fmt(r.last)}` : ""}</span>
          <span className="block text-sm mt-1"><b data-testid={`comp-count-${i}`}>{r.count.toLocaleString("de-DE")}</b> {r.count === 1 ? "Mitglied" : "Mitglieder"} · <b className={minus ? "text-red-600" : "text-emerald-600"} data-testid={`comp-sum-${i}`}>{pts(r.sum)} Punkte</b> {minus ? "abgezogen" : "gutgeschrieben"}</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <NameList reason={r.reason} />}
    </div>
  );
}

export function CompensationsAdmin() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/compensations").then(({ data }) => setRows(data)).catch((e) => { setRows([]); toast.error(formatErr(e)); }); }, []);
  return (
    <div className="space-y-3" data-testid="compensations-admin">
      <h2 className="text-lg font-extrabold">Gutschriften & Abzüge</h2>
      <p className="text-sm text-slate-500">Alle Sammel-Gutschriften und Korrekturen an viele Mitglieder – tippe auf eine Aktion für die Namensliste.</p>
      {!rows ? <Loader2 className="w-6 h-6 animate-spin text-rose-400" /> : rows.length ? rows.map((r, i) => <CompRow key={r.reason} r={r} i={i} />) : <p className="text-sm text-slate-400" data-testid="comp-empty">Noch keine Gutschriften oder Abzüge.</p>}
    </div>
  );
}
